import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import AdminUserManagement from '../components/AdminUserManagement'
import { fetchOverallMetrics, fetchStatusDistribution } from '../services/metricsApi'
import { fetchVisitorReport, downloadVisitorCSV, downloadAppointmentCSV } from '../services/reportsApi'
import { getAllFeedback, getFeedbackStats } from '../services/feedbackApi'
import { useSocket } from '../hooks/useSocket'
import { loginUser, logout, getAuthUser, getAuthToken } from '../services/authService'
import { apiUrl } from '../services/apiConfig'
import { requestNotificationPermission, registerInstallation, onMessageReceived } from '../services/fcmService'


export default function Admin() {
  const navigate = useNavigate()
  const { socket, isConnected, backendStatus, joinAdminRoom } = useSocket()
  const storedUser = getAuthUser()
  const isAdminUser = !!getAuthToken() && storedUser?.role === 'admin'
  const [isLoggedIn, setIsLoggedIn] = useState(isAdminUser)
  const [currentUser, setCurrentUser] = useState(isAdminUser ? storedUser : null)
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  
  // Metrics state
  const [metrics, setMetrics] = useState(null)
  const [statusDistribution, setStatusDistribution] = useState(null)
  
  // Data state
  const [activeTab, setActiveTab] = useState('dashboard')
  const [visitors, setVisitors] = useState([])
  const [visitorsLoading, setVisitorsLoading] = useState(false)
  const [filterStatus, setFilterStatus] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedVisitor, setSelectedVisitor] = useState(null)
  
  // Feedback state
  const [feedback, setFeedback] = useState([])
  const [feedbackStats, setFeedbackStats] = useState(null)
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const [feedbackFilter, setFeedbackFilter] = useState('')

  // Fetch metrics when logged in
  useEffect(() => {
    if (isLoggedIn) {
      loadMetrics()
      joinAdminRoom()

      // Register for FCM push notifications
      requestNotificationPermission().then(granted => {
        if (granted) {
          registerInstallation(currentUser?.id || 'admin', 'admin')
        }
      })

      // Refresh when a push arrives (it is shown in the notification panel by the service worker)
      return onMessageReceived((payload) => {
        console.log('FCM message received in Admin:', payload)
        loadMetrics()
        if (activeTab === 'visitors') {
          loadVisitors()
        }
      })
    }
  }, [isLoggedIn])

  // Socket event listeners
  useEffect(() => {
    if (!socket || !isConnected) return

    const handleVisitorRegistered = (visitor) => {
      loadMetrics()
      if (activeTab === 'visitors') {
        loadVisitors()
      }
    }

    const handleAppointmentAccepted = (visitor) => {
      loadMetrics()
      if (activeTab === 'visitors') {
        loadVisitors()
      }
    }

    const handleAppointmentRejected = (visitor) => {
      loadMetrics()
      if (activeTab === 'visitors') {
        loadVisitors()
      }
    }

    const handleVisitorCheckedIn = (visitor) => {
      loadMetrics()
      if (activeTab === 'visitors') {
        loadVisitors()
      }
    }

    const handleVisitorCheckedOut = (visitor) => {
      loadMetrics()
      if (activeTab === 'visitors') {
        loadVisitors()
      }
    }

    socket.on('visitor-registered', handleVisitorRegistered)
    socket.on('appointment-accepted', handleAppointmentAccepted)
    socket.on('appointment-rejected', handleAppointmentRejected)
    socket.on('visitor-checked-in', handleVisitorCheckedIn)
    socket.on('visitor-checked-out', handleVisitorCheckedOut)

    return () => {
      socket.off('visitor-registered', handleVisitorRegistered)
      socket.off('appointment-accepted', handleAppointmentAccepted)
      socket.off('appointment-rejected', handleAppointmentRejected)
      socket.off('visitor-checked-in', handleVisitorCheckedIn)
      socket.off('visitor-checked-out', handleVisitorCheckedOut)
    }
  }, [socket, isConnected, activeTab])

  useEffect(() => {
    setError('')  // Clear errors when switching tabs
    if (isLoggedIn && activeTab === 'visitors') {
      loadVisitors()
    }
    if (isLoggedIn && activeTab === 'feedback') {
      loadFeedback()
    }
  }, [activeTab, filterStatus, startDate, endDate, feedbackFilter])

  const loadMetrics = async () => {
    try {
      const [metricsData, statusData] = await Promise.all([
        fetchOverallMetrics(),
        fetchStatusDistribution()
      ])
      setMetrics(metricsData)
      setStatusDistribution(statusData)
    } catch (err) {
      console.error('Failed to load metrics:', err)
    }
  }

  const handleRefresh = () => {
    loadMetrics()
    if (activeTab === 'visitors') {
      loadVisitors()
    }
    if (activeTab === 'feedback') {
      loadFeedback()
    }
  }



  const loadVisitors = async () => {
    try {
      setVisitorsLoading(true)
      setError('')
      const params = new URLSearchParams()
      if (filterStatus) params.append('status', filterStatus)
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      
      const queryString = params.toString()
      const url = queryString ? apiUrl(`/visitors?${queryString}`) : apiUrl('/visitors')
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch visitors')
      const data = await response.json()
      setVisitors(data)
    } catch (err) {
      console.error('Failed to fetch visitors:', err)
      setError('Failed to load visitors')
    } finally {
      setVisitorsLoading(false)
    }
  }

  const loadFeedback = async () => {
    try {
      setFeedbackLoading(true)
      setError('')
      const [feedbackData, statsData] = await Promise.all([
        getAllFeedback(),
        getFeedbackStats()
      ])
      setFeedback(feedbackData)
      setFeedbackStats(statsData)
    } catch (err) {
      console.error('Failed to load feedback:', err)
      setError('Failed to load feedback')
    } finally {
      setFeedbackLoading(false)
    }
  }

  const getFilteredFeedback = () => {
    if (!feedbackFilter) return feedback
    return feedback.filter(f => f.rating === parseInt(feedbackFilter))
  }

  const handleDownloadVisitorCSV = () => {
    downloadVisitorCSV(startDate, endDate)
  }

  const handleDownloadAppointmentCSV = () => {
    downloadAppointmentCSV(startDate, endDate)
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoginLoading(true)
    setError('')

    try {
      const result = await loginUser(formData.email, formData.password, 'admin')
      setIsLoggedIn(true)
      setCurrentUser(result.user)
      setFormData({ email: '', password: '' })
    } catch (err) {
      setError(err.message || 'Invalid credentials')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    setIsLoggedIn(false)
    setCurrentUser(null)
    setFormData({ email: '', password: '' })
    navigate('/')
  }



  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <button
              onClick={() => navigate('/')}
              className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </button>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center mb-8">
                <img src="/logo.png" alt="igus Logo" className="mx-auto mb-4 h-16 w-auto" />
                <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
                <p className="mt-2 text-sm text-gray-600">Enter your credentials to access the admin panel</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                    Admin Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="admin@igus.net"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                    required
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full rounded-lg bg-saffron-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-saffron-700 focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loginLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              <div className="mt-6 border-t border-gray-200 pt-6">
                <p className="text-xs text-gray-500 text-center">
                  <span className="block mb-2">Contact your administrator</span>
                  <span className="block mb-3">if you need assistance with login credentials</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="igus Logo" className="h-10 w-auto" />
            <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-700">{currentUser?.name}</span>
            <span className={`flex h-2 w-2 rounded-full ${backendStatus === 'connected' ? 'bg-green-500' : backendStatus === 'error' ? 'bg-yellow-500' : 'bg-red-500'}`} />
            <span className="text-xs text-gray-500">{backendStatus === 'connected' ? 'Connected' : backendStatus === 'error' ? 'Config Error' : 'Disconnected'}</span>
            <button
              onClick={handleRefresh}
              disabled={loading || visitorsLoading || feedbackLoading}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Welcome, Admin</h2>
          <p className="mt-1 text-gray-600">Manage visitors, appointments, and system settings</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex gap-2 rounded-xl bg-white p-1 shadow-sm ring-1 ring-saffron-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex-1 rounded-lg px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'bg-saffron-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-saffron-50 hover:text-saffron-700'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('visitors')}
              className={`flex-1 rounded-lg px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'visitors'
                  ? 'bg-saffron-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-saffron-50 hover:text-saffron-700'
              }`}
            >
              Visitors & Appointments
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 rounded-lg px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'users'
                  ? 'bg-saffron-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-saffron-50 hover:text-saffron-700'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`flex-1 rounded-lg px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'feedback'
                  ? 'bg-saffron-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-saffron-50 hover:text-saffron-700'
              }`}
            >
              Feedback
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {activeTab === 'dashboard' && (
          <>
            {/* Metrics Cards */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Visitors</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">{metrics?.totalVisitors || 0}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-saffron-100">
                    <svg className="h-6 w-6 text-saffron-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Today's Visitors</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">{metrics?.todayVisitors || 0}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Active Visitors</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">{metrics?.activeVisitors || 0}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Pending Approvals</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">{metrics?.pendingApprovals || 0}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                    <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Today Appointments</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">{metrics?.todayAppointments || 0}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                    <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Distribution */}
            {statusDistribution && (
              <div className="mt-6 rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Appointment Status Distribution</h3>
                <div className="grid gap-4 sm:grid-cols-5">
                  <div className="flex items-center gap-3">
                    <div className="h-3 w-3 rounded-full bg-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Pending</p>
                      <p className="text-lg font-semibold text-gray-900">{statusDistribution.pending}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-3 w-3 rounded-full bg-green-500" />
                    <div>
                      <p className="text-sm text-gray-500">Accepted</p>
                      <p className="text-lg font-semibold text-gray-900">{statusDistribution.accepted}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-3 w-3 rounded-full bg-red-500" />
                    <div>
                      <p className="text-sm text-gray-500">Rejected</p>
                      <p className="text-lg font-semibold text-gray-900">{statusDistribution.rejected}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-3 w-3 rounded-full bg-blue-500" />
                    <div>
                      <p className="text-sm text-gray-500">Checked In</p>
                      <p className="text-lg font-semibold text-gray-900">{statusDistribution.checked_in}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-3 w-3 rounded-full bg-purple-500" />
                    <div>
                      <p className="text-sm text-gray-500">Checked Out</p>
                      <p className="text-lg font-semibold text-gray-900">{statusDistribution.checked_out}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'visitors' && (
          <>
            {/* Filters */}
            <div className="mb-6 rounded-xl bg-white p-4 shadow-sm ring-1 ring-saffron-200">
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="checked_in">Checked In</option>
                    <option value="checked_out">Checked Out</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={handleDownloadVisitorCSV}
                    className="flex-1 rounded-lg bg-saffron-600 px-4 py-2 text-sm font-medium text-white hover:bg-saffron-700"
                  >
                    Download CSV
                  </button>
                  <button
                    onClick={handleDownloadAppointmentCSV}
                    className="flex-1 rounded-lg border border-saffron-600 px-4 py-2 text-sm font-medium text-saffron-700 hover:bg-saffron-50"
                  >
                    Download Appointments
                  </button>
                </div>
              </div>
            </div>

            {/* Visitors Table */}
            <div className="rounded-xl bg-white shadow-sm ring-1 ring-saffron-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Purpose</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Appointment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {visitorsLoading ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading...
                        </td>
                      </tr>
                    ) : visitors.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                          No visitors found
                        </td>
                      </tr>
                    ) : (
                      visitors.map((visitor) => (
                        <tr key={visitor.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{visitor.full_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{visitor.organization}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{visitor.employee_name || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{visitor.purpose}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {visitor.appointment_date && visitor.appointment_time ? `${visitor.appointment_date} ${visitor.appointment_time}` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                              visitor.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                              visitor.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              visitor.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              visitor.status === 'checked_in' ? 'bg-blue-100 text-blue-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {visitor.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(visitor.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => setSelectedVisitor(visitor)}
                              className="rounded-lg bg-saffron-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-saffron-700"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}


        {activeTab === 'users' && (
          <AdminUserManagement />
        )}

        {activeTab === 'feedback' && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Visitor Feedback</h2>
              <p className="mt-1 text-gray-600">View and manage visitor feedback and ratings</p>
            </div>

            {/* Feedback Statistics */}
            {feedbackStats && (
              <div className="grid gap-4 sm:grid-cols-5 mb-6">
                <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-saffron-200">
                  <p className="text-sm font-medium text-gray-500">Total Feedback</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{feedbackStats.totalFeedback}</p>
                </div>
                <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-saffron-200">
                  <p className="text-sm font-medium text-gray-500">Average Rating</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{feedbackStats.averageRating.toFixed(1)}</p>
                </div>
                <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-saffron-200">
                  <p className="text-sm font-medium text-gray-500">5 Star</p>
                  <p className="mt-2 text-3xl font-bold text-yellow-500">{feedbackStats.ratings.rating_5 || 0}</p>
                </div>
                <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-saffron-200">
                  <p className="text-sm font-medium text-gray-500">4 Star</p>
                  <p className="mt-2 text-3xl font-bold text-yellow-500">{feedbackStats.ratings.rating_4 || 0}</p>
                </div>
                <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-saffron-200">
                  <p className="text-sm font-medium text-gray-500">Below 4 Star</p>
                  <p className="mt-2 text-3xl font-bold text-orange-500">{(feedbackStats.ratings.rating_1 || 0) + (feedbackStats.ratings.rating_2 || 0) + (feedbackStats.ratings.rating_3 || 0)}</p>
                </div>
              </div>
            )}

            {/* Filter */}
            <div className="mb-6 flex gap-4">
              <select
                value={feedbackFilter}
                onChange={(e) => setFeedbackFilter(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
              >
                <option value="">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            {/* Feedback List */}
            <div className="rounded-xl bg-white shadow-sm ring-1 ring-saffron-200 overflow-hidden">
              {feedbackLoading ? (
                <div className="px-6 py-8 text-center text-gray-500">Loading feedback...</div>
              ) : getFilteredFeedback().length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">No feedback found</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {getFilteredFeedback().map((item) => (
                    <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.visitor_name}</h4>
                          <p className="text-sm text-gray-500">{item.visitor_email}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`h-5 w-5 ${
                                i < item.rating ? 'text-yellow-400 fill-current' : 'text-gray-300 fill-current'
                              }`}
                              viewBox="0 0 20 20"
                            >
                              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                      {item.feedback && (
                        <p className="text-sm text-gray-700 mb-2 bg-gray-50 p-3 rounded">{item.feedback}</p>
                      )}
                      <p className="text-xs text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Visitor Details Modal */}
      {selectedVisitor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Visitor Details</h2>
              <button
                onClick={() => setSelectedVisitor(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Full Name</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Gender</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.gender}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Phone</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.phone}</p>
                  </div>
                </div>
              </div>

              {/* Visit Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Visit Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Organization</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.organization}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Purpose</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.purpose}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Appointment Date</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.appointment_date}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Appointment Time</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.appointment_time}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm font-medium text-gray-500">Reason</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.reason}</p>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              {selectedVisitor.additional_notes && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h3>
                  <p className="text-gray-900">{selectedVisitor.additional_notes}</p>
                </div>
              )}

              {/* Signature */}
              {selectedVisitor.signature && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Signature</h3>
                  <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                    <img
                      src={selectedVisitor.signature}
                      alt="Visitor Signature"
                      className="max-h-32 mx-auto"
                    />
                  </div>
                </div>
              )}

              {/* Check-in/Check-out Times */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Check-in/Check-out Times</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Check-in Time</p>
                    <p className="mt-1 text-gray-900">
                      {selectedVisitor.check_in_time ? new Date(selectedVisitor.check_in_time).toLocaleString() : 'Not checked in'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Check-out Time</p>
                    <p className="mt-1 text-gray-900">
                      {selectedVisitor.check_out_time ? new Date(selectedVisitor.check_out_time).toLocaleString() : 'Not checked out'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Status</h3>
                <span className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-medium ${
                  selectedVisitor.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                  selectedVisitor.status === 'checked_out' ? 'bg-gray-100 text-gray-800' :
                  selectedVisitor.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                  selectedVisitor.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedVisitor.status.replace(/_/g, ' ')}
                </span>
              </div>

              {/* Close Button */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setSelectedVisitor(null)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}
          
